<template>
  <!-- Actions -->
  <HeaderBar :modifiers="['transparent', 'form']">
    <div class="header-bar__title typo-title">
      {{ $t('nav.profile') }}
    </div>
    <template #right>
      <a
        class="button"
        @click="$router.back()"
      >
        {{ $t('general.cancel') }}
      </a>
      <FormSubmitButton
        :isSaving="saving"
        @click="updateUserDetails"
      />
    </template>
  </HeaderBar>
  <!-- Form -->
  <div class="form-layout">
    <!-- Details info -->
    <div class="form-layout__heading">
      <h2 class="form-layout__title typo-small-title">
        {{ $t('profile.details_title') }}
      </h2>
      <p class="form-layout__description typo-small-title">
        {{ $t('profile.details_description') }}
      </p>
    </div>
    <!-- Form fields -->
    <div class="form-layout__content">
      <div class="form-layout__row">
        <div class="form-layout__column">
          <FormRow
            :errors="errors && errors.user.first_name"
            :label="$t('activerecord.attributes.user.first_name')"
            labelFor="first_name"
            :modifiers="['no-padding']"
          >
            <div class="form-row__input">
              <div :class="{ 'input': true, 'input--error': errors && errors.user.first_name }">
                <input
                  id="first_name"
                  v-model="currentUser.first_name"
                  type="text"
                >
              </div>
            </div>
          </FormRow>
        </div>
        <div class="form-layout__column">
          <FormRow
            :errors="errors && errors.user.last_name"
            :label="$t('activerecord.attributes.user.last_name')"
            labelFor="last_name"
            :modifiers="['no-padding']"
          >
            <div class="form-row__input">
              <div :class="{ 'input': true, 'input--error': errors && errors.user.last_name }">
                <input
                  id="last_name"
                  v-model="currentUser.last_name"
                  type="text"
                >
              </div>
            </div>
          </FormRow>
        </div>
      </div>
      <FormRow
        :errors="errors && errors.user.email"
        :label="$t('activerecord.attributes.user.email')"
        labelFor="email"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input">
          <div :class="{ 'input': true, 'input--error': errors && errors.user.email }">
            <input
              id="email"
              v-model="currentUser.email"
              type="text"
              :disabled="true"
            >
          </div>
        </div>
      </FormRow>
      <FormRow
        :label="$t('activerecord.attributes.user.contact_number')"
        labelFor="contact_number"
        :modifiers="['no-padding']"
        :optional="true"
      >
        <div class="form-row__input">
          <div class="input">
            <input
              id="contact_number"
              v-model="currentUser.contact_number"
              type="text"
            >
          </div>
        </div>
      </FormRow>
      <FormRow
        class="language-select"
        :label="$t('activerecord.attributes.user.language')"
        labelFor="language"
        :modifiers="['no-padding']"
        :optional="true"
      >
        <div class="form-row__input">
          <Select
            v-model="currentUser.locale"
            class="select--input select--base"
            :items="languageOptions"
            :placeholder="$t('activerecord.attributes.user.select_language')"
            :showCurrentSelection="true"
          />
        </div>
      </FormRow>
    </div>
  </div>
  <!-- Change password action -->
  <div class="form-layout">
    <div class="form-layout__content">
      <router-link
        class="button button--inline"
        :to="{ name: 'userPassword' }"
      >
        {{ $t('profile.change_password') }}
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { onMounted, provide, reactive, ref, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import useAjax from '@/functions/use_ajax';
import useEmitter from '@/functions/use_emitter';
import I18n from 'i18n-js';

// Components
import HeaderBar from '@/components/generic/header_bar.vue';
import FormRow from '@/components/form/row.vue';
import Select from '@/components/form/select.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';

const createNewUser = () => ({
  id: null,
  first_name: '',
  last_name: '',
  contact_number: null,
  email: null,
  locale: null,
  full_name: null,
  last_organisation_id: null
});

const router = useRouter();
const ajax = useAjax();
const currentUser = reactive(createNewUser());
const emitter = useEmitter();

const errors = ref(null);
const saving = ref(false);
const isSaved = ref(false);

/**
 * Language options
 */
const languageOptions = [
  { value: 'en', label: I18n.t('activerecord.attributes.user.english') },
  { value: 'es', label: I18n.t('activerecord.attributes.user.spanish') },
  { value: 'pt-BR', label: I18n.t('activerecord.attributes.user.portuguese') },
];

onMounted(async() => (Object.assign(currentUser, await loadCurrentUser())));

async function loadCurrentUser() {
  let { ok, data } = await ajax('/users/edit');
  if (ok) {
    return data;
  }
}

async function updateUserDetails() {
  saving.value = true;
  let { ok, data } = await ajax('/users', {
    method: 'PUT',
    body: {
      user: {
        first_name: currentUser.first_name,
        last_name: currentUser.last_name,
        email: currentUser.email,
        contact_number: currentUser.contact_number,
        locale: currentUser.locale,
      },
    },
  });
  if (ok) {
    isSaved.value = !isSaved.value;
    router.push({ name: 'operations', params: { organisationId: currentUser.last_organisation_id } });
    emitter.emit('updateLocale', data.locale);
    errors.value = null;
  } else {
    errors.value = data;
  }

  saving.value = false;
}

onBeforeUnmount(() => {
  if (!isSaved.value) return;
  const newCurrentUser = { ...currentUser, full_name: `${currentUser.first_name} ${currentUser.last_name}` };
  provide('currentUser', newCurrentUser);
});
</script>
